import { Switch } from "@headlessui/react";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { useUpdateMutation } from "../api/genericHooks";
import { GeneratedChapterStatusBadge } from "../components/Badges/GeneratedChapterStatusBadge";
import { AddPicturesButton } from "../components/Buttons/AddPicturesButton";
import { GeneratedChapterNavButton } from "../components/Buttons/GeneratedChapterNavButton";
import { AddPicturesModal } from "../components/Modals/AddPicturesModal";
import { RichEditor } from "../components/RichEditor";
import { BookContext } from "../contexts/BookContext";
import { selectUser } from "../store/slices/auth";
import { Chapter } from "../types/models";

type GeneratedChapterData = {
  title: string;
  content: string;
  completed: boolean;
  chapter_id: number;
};

// Resolves the route params, then renders the editor once everything exists.
// Keeping the early returns here lets GeneratedChapterEditor call its hooks
// unconditionally (rules of hooks).
export const GeneratedChapter = () => {
  const { book, chapters } = useContext(BookContext);
  const { chapterNum } = useParams();
  const user = useSelector(selectUser);

  if (!chapterNum || !book?.id || !user) return null;

  const chapter = chapters.find(
    (chapter) => chapter.number === parseInt(chapterNum)
  );
  if (!chapter) return null;

  return <GeneratedChapterEditor chapter={chapter} />;
};

const GeneratedChapterEditor = ({ chapter }: { chapter: Chapter }) => {
  const { t } = useTranslation();
  const { chapters, generated_chapters, refresh } = useContext(BookContext);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddImagesModalOpen, setIsAddImagesModalOpen] = useState(false);

  const generatedChapter = generated_chapters.find(
    (generated_chapter) => generated_chapter.chapter_id === chapter.id
  );

  const [completed, setCompleted] = useState(
    generatedChapter?.completed || false
  );

  // Form setup with React Hook Form
  const { register, handleSubmit, setValue, watch, control, reset } =
    useForm<GeneratedChapterData>({
      defaultValues: {
        title: generatedChapter?.title || "",
        content: generatedChapter?.content || "",
        completed: generatedChapter?.completed || false,
        chapter_id: generatedChapter?.chapter_id || chapter.id,
      },
    });

  const content = watch("content");

  // Query methods
  const { mutateAsync: updateGeneratedChapterMutateAsync } = useUpdateMutation({
    route: "/generated_chapters/:id",
    pathParams: { id: generatedChapter?.id },
  });

  useEffect(() => {
    setCompleted(generatedChapter?.completed || false);
  }, [generatedChapter]);

  useEffect(() => {
    reset({
      title: generatedChapter?.title || "",
      content: generatedChapter?.content || "",
      completed: generatedChapter?.completed || false,
      chapter_id: generatedChapter?.chapter_id || chapter.id,
    });
  }, [generatedChapter, reset, chapter.id]);

  // Booleans
  const isFirstChapter = chapter.number === 1;
  const isLastChapter = chapter.number === chapters.length;

  // Prev Chapter & Question
  const prevChapterNum = isFirstChapter ? 1 : chapter.number - 1;

  // Next Chapter & Question
  const nextChapterNum = isLastChapter ? chapter.number : chapter.number + 1;

  const onSubmit = (data: GeneratedChapterData) => {
    updateGeneratedChapterMutateAsync({ generated_chapter: data }).then(() => {
      reset();
      toast.success(t("generatedChapter.updated"));
      refresh();
      if (isEditMode) {
        setIsEditMode(false);
      }
    });
  };

  const setCompletedAndSubmit = (completedChecked: boolean) => {
    setCompleted(completedChecked);
    setValue("completed", completedChecked);
    handleSubmit(onSubmit)();
  };

  return (
    <>
      <div className="px-3 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-8 flex flex-1">
        <form
          className="bg-white flex flex-1 flex-col justify-between shadow-md rounded"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div>
            <div className="flex flex-col xl:flex-row p-3 sm:p-4">
              <div className="flex flex-1 flex-col xl:flex-row xl:items-center xl:space-x-6 mr-0 xl:mr-6">
                {isEditMode ? (
                  <input
                    className="block w-full xl:w-4/6 rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-delicate-600 mb-3 xl:mb-0"
                    defaultValue={generatedChapter?.title}
                    placeholder={t("generatedChapter.title")}
                    disabled={!isEditMode}
                    {...register("title")}
                  />
                ) : (
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-700 w-max-3/4 flex-1 mb-3 xl:mb-0">
                    {generatedChapter?.title}
                  </h1>
                )}
                <div>
                  <GeneratedChapterStatusBadge
                    completed={generatedChapter?.completed || false}
                  />
                </div>
              </div>
              <div className="flex flex-col items-start xl:items-end justify-center mt-4 xl:mt-0">
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                  <div className="flex items-center space-x-3">
                    <p className="text-light text-sm text-slate-500">
                      {t("generatedChapter.markAsCompleted")}
                    </p>
                    <Switch
                      checked={completed}
                      onChange={setCompletedAndSubmit}
                      className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                    >
                      <span className="sr-only">Use setting</span>
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute size-full rounded bg-white"
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute mx-auto h-4 w-9 rounded-full bg-gray-200 transition-colors duration-200 ease-in-out group-data-[checked]:bg-herb-600"
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute left-0 inline-block size-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                      />
                    </Switch>
                  </div>
                  <div className="flex space-x-2 sm:space-x-3">
                    {isEditMode ? (
                      <>
                        <AddPicturesButton
                          onClick={() => setIsAddImagesModalOpen(true)}
                        />
                        <button
                          className="flex bg-rust-50 text-white border hover:bg-rust-10 hover:text-rust-50 font-light py-2 px-3 sm:px-4 rounded text-sm sm:text-base"
                          type="submit"
                        >
                          <PlusCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3" />
                          {t("generatedChapter.save")}
                        </button>
                      </>
                    ) : (
                      <button
                        className="flex bg-rust-50 text-white border hover:bg-rust-10 hover:text-rust-50 font-light py-2 px-3 sm:px-4 rounded text-sm sm:text-base"
                        type="button"
                        onClick={() => setIsEditMode(true)}
                      >
                        <PlusCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3" />
                        {t("generatedChapter.edit")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col flex-1 p-3 sm:p-4">
            {isEditMode ? (
              <>
                {/* Utilisons Controller de react-hook-form pour le double bind */}
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <RichEditor value={field.value} onChange={field.onChange} />
                  )}
                />
                <div className="flex flex-row justify-end mt-4">
                  <p className="text-slate-300 text-xs sm:text-sm">
                    {t("question.answerLength", {
                      length: content?.length || 0,
                    })}
                  </p>
                </div>
              </>
            ) : (
              <>
                {generatedChapter?.content && (
                  <div
                    className="text-slate-700 text-base sm:text-lg prose prose-sm w-full sm:w-4/5 lg:w-3/5 text-justify mx-auto shadow-[0px_0px_15px_rgba(228,237,242,0.9)] rounded p-4 sm:p-6 lg:p-10"
                    dangerouslySetInnerHTML={{
                      __html: generatedChapter.content,
                    }}
                  />
                )}
              </>
            )}
          </div>

          <div className="flex flex-row justify-between px-3 sm:px-4 py-3 mt-4 gap-3 sm:gap-0">
            <div>
              {!isFirstChapter && (
                <GeneratedChapterNavButton
                  onClick={() => {
                    handleSubmit(onSubmit)();
                    setIsEditMode(false);
                    window.scrollTo(0, 0);
                  }}
                  mode="prev"
                  nextPrevChapterNum={prevChapterNum}
                />
              )}
            </div>
            <div>
              {!isLastChapter && (
                <GeneratedChapterNavButton
                  onClick={() => {
                    handleSubmit(onSubmit)();
                    setIsEditMode(false);
                    window.scrollTo(0, 0);
                  }}
                  mode="next"
                  nextPrevChapterNum={nextChapterNum}
                />
              )}
            </div>
          </div>
        </form>
      </div>
      <AddPicturesModal
        open={isAddImagesModalOpen}
        setOpen={setIsAddImagesModalOpen}
        chapter_id={chapter.id}
      />
    </>
  );
};
